import React, { useState } from 'react';

import { AxiosResponse } from 'axios';

import Header from 'components/Header';
import Loader from 'components/Loader';
import { Mobile, Desktop } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Menu from 'containers/UserMenu';
import useIncompleteCompanyProfileMessage from 'hooks/useIncompleteCompanyProfileMessage';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { ConfirmMessage } from 'models/confirmMessage';
import { RepprovalReason } from 'models/repprovalReason';
import { RequestedItem } from 'models/requestHistory';
import { Response } from 'models/response';
import { TravelsApprovalRequest, TravelAwaitingApprove, TravelsApprovalResponse } from 'models/travelApproved';
import { AccountResponse } from 'models/user';
import { extractErrorMessage, htmlT } from 'utils/generic';
import { goBackToApp } from 'utils/webViewUtils';

import SolicitationApproved from '../SolicitationApproved';
import SolicitationReproved from '../SolicitationReproved';
import FilterButton from './components/FilterButton';
import ListAwaiting from './components/ListAwaiting';
import Message from './components/MessageHeader';
import { getErrorMessage } from './mapper';
import { Container, Body } from './styles';

interface Props {
  isLoading: boolean;
  getTravels: (info: TravelsApprovalRequest) => Promise<Response<TravelsApprovalResponse>>;
  changeStatus: (
    travelId: string,
    isApproval: boolean,
    approverId: string,
    reason?: RepprovalReason,
  ) => Promise<AxiosResponse<Response<any>>>;
  accountResult: AccountResponse | null;
  pendingTravels?: TravelAwaitingApprove[];
  reasons?: RepprovalReason[];
}

const Dashboard = ({ isLoading, getTravels, changeStatus, accountResult, pendingTravels, reasons }: Props) => {
  const { t } = useStoreTranslation();
  const toast = useToast();
  const { isLoadingCompany } = useIncompleteCompanyProfileMessage();

  const [isShow, setIsShow] = useState(false);
  const [showApproved, setShowApproved] = useState(false);
  const [showReproved, setShowReproved] = useState(false);
  const [showConfirmApproved, setShowConfirmApproved] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<ConfirmMessage>({});
  const [showDetails, setShowDetails] = useState<string | undefined>();
  const [travel, setTravel] = useState<RequestedItem>();

  const openBottomSheet = () => {
    setIsShow(true);
  };

  const closeBottomSheet = () => {
    setIsShow(false);
  };

  const userInfo: TravelsApprovalRequest = {
    userId: accountResult?.id || '',
    companyId: accountResult?.companyId || '',
  };

  const fetchTravels = () =>
    getTravels(userInfo).catch((error) => {
      toast.add({
        type: 'danger',
        message: extractErrorMessage(error) || t('defaultError.default'),
      });
    });

  const setErrorMessage = (message: ConfirmMessage, hasButton?: boolean) => {
    setShowConfirmApproved(true);
    setConfirmMessage(hasButton ? { ...message, button: t('travelApproval.buttonDetails') } : message);
  };

  const handleChangeStatus = async (travel: RequestedItem, isApproval: boolean, reason?: RepprovalReason) => {
    setTravel(travel);
    try {
      const {
        data: { errors, succeeded },
      } = await changeStatus(travel.travelId, isApproval, userInfo.userId, reason);

      if (succeeded) {
        if (isApproval) setShowApproved(true);
        else setShowReproved(true);

        fetchTravels();
      } else {
        const msg = getErrorMessage(errors, t('locale'));
        setErrorMessage(
          {
            title: msg.message.title ? t(msg.message.title) : '',
            text: msg.message.text
              ? msg.props
                ? htmlT(t, `${msg.message.text}`, msg.props)
                : htmlT(t, `${msg.message.text}`)
              : '',
          },
          msg.button ?? false,
        );
      }
    } catch (error) {
      const msg = getErrorMessage(error.response.data.Errors || error.response.data.errors, t('locale'));
      setErrorMessage(
        {
          title: msg.message.title ? t(msg.message.title) : '',
          text: msg.message.text
            ? msg.props
              ? htmlT(t, `${msg.message.text}`, msg.props)
              : htmlT(t, `${msg.message.text}`)
            : '',
        },
        msg.button ?? false,
      );
    }
  };

  const handleConfirm = async (travel?: RequestedItem) => {
    if (!travel) return;
    setShowConfirmApproved(false);
    setTravel(travel);
    setShowDetails(travel.travelId);
  };

  if (!(showApproved || showReproved) && (isLoading || isLoadingCompany)) return <Loader fullScreen />;

  return (
    <>
      <Container>
        <Mobile>
          <Header openMenu={openBottomSheet} goBack={goBackToApp} />
        </Mobile>
        <Desktop>
          <ScreenHeader
            title={t('userMenu.dashboardTitle')}
            subtitle={t('userMenu.dashboardDetail')}
            right={<FilterButton />}
          />
        </Desktop>

        <Body>
          <Mobile>
            <Message name={accountResult?.name || 'User'} />
          </Mobile>
          <ListAwaiting
            data={pendingTravels}
            onApprove={(travel: RequestedItem) => handleChangeStatus(travel, true)}
            onReject={(travel: RequestedItem, reason?: RepprovalReason) => handleChangeStatus(travel, false, reason)}
            onConfirmClose={() => {
              setShowConfirmApproved(false);
              fetchTravels();
            }}
            onConfirmApprove={() => handleConfirm(travel)}
            onDetailsClose={() => {
              setShowDetails(undefined);
              fetchTravels();
            }}
            showConfirm={showConfirmApproved}
            confirmMessage={confirmMessage}
            showDetails={showDetails}
            reasons={reasons}
          />
        </Body>

        <Menu show={isShow} onClose={closeBottomSheet} />
      </Container>

      <SolicitationApproved show={showApproved} travel={travel} onConfirm={() => setShowApproved(false)} />
      <SolicitationReproved show={showReproved} travel={travel} onConfirm={() => setShowReproved(false)} />
    </>
  );
};

export default Dashboard;
